/** Shared canvas drawing for the recent-vs-prior trend-window overlay — the amber
 *  divider and its label pill — used across the Barrier Effectiveness and Report
 *  Clustering time-series charts (see the project window-band-styling convention).
 *
 *  These are pure 2D-context helpers, so they work both from a Chart.js plugin
 *  (passing `chartArea` edges) and from raw canvas drawing (passing pixel coords).
 *
 *  NB: the BE canvas-heatmap pills use a larger-radius `arcTo` variant tuned for the
 *  scrollable grid; they are intentionally NOT unified here. Only the divider is
 *  shared with the heatmap. */

/** Translucent blue fill for the recent-vs-prior comparison window. */
export const TREND_BAND_FILL = 'rgba(33, 150, 243, 0.13)';
/** Dashed edge color delimiting the window band. */
export const TREND_BAND_EDGE = 'rgba(33, 150, 243, 0.55)';

/** Shaded window band over `[band.left, band.right]` plus dashed vertical edges at
 *  the given x positions. Edges that coincide with the plot boundary (`plotLeft` /
 *  `plotRight`) are skipped — e.g. the "now" edge sitting on the plot's edge. */
export const drawTrendWindowBand = (
    ctx: CanvasRenderingContext2D,
    band: {left: number; right: number; top: number; bottom: number},
    edges: number[],
    plotLeft: number,
    plotRight: number,
): void => {
    if (band.right <= band.left) return;
    ctx.fillStyle = TREND_BAND_FILL;
    ctx.fillRect(band.left, band.top, band.right - band.left, band.bottom - band.top);
    ctx.strokeStyle = TREND_BAND_EDGE;
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    for (const px of edges) {
        if (px > plotLeft + 0.5 && px < plotRight - 0.5) {
            ctx.beginPath();
            ctx.moveTo(px, band.top);
            ctx.lineTo(px, band.bottom);
            ctx.stroke();
        }
    }
};

/** Vertical divider: a 5px white halo (so it reads over dots/cells) under a 2px
 *  amber line, from `(x, top)` to `(x, bottom)`. Leaves the amber stroke state set,
 *  matching the previous inline behavior; callers that care wrap this in save/restore. */
export const drawTrendWindowDivider = (
    ctx: CanvasRenderingContext2D,
    x: number,
    top: number,
    bottom: number,
): void => {
    ctx.setLineDash([]);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.strokeStyle = '#ff6f00';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
};

/** Rounded amber label pill centered on `x`, sitting just above `areaTop` and
 *  clamped horizontally to `[clampLeft, clampRight]`. Used by the BE/RC Chart.js
 *  trend-window overlays (drawn into the reserved padding strip above the plot). */
export const drawTrendWindowPill = (
    ctx: CanvasRenderingContext2D,
    x: number,
    areaTop: number,
    clampLeft: number,
    clampRight: number,
    text: string,
): void => {
    ctx.font = 'bold 10px sans-serif';
    const padX = 6;
    const tw = ctx.measureText(text).width;
    const pillW = tw + padX * 2;
    const pillH = 14;
    const pillX = Math.min(Math.max(x - pillW / 2, clampLeft), clampRight - pillW);
    const pillY = Math.max(2, areaTop - pillH - 2);
    const r = 4;
    ctx.fillStyle = '#ff6f00';
    ctx.beginPath();
    ctx.moveTo(pillX + r, pillY);
    ctx.lineTo(pillX + pillW - r, pillY);
    ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
    ctx.lineTo(pillX + pillW, pillY + pillH - r);
    ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH);
    ctx.lineTo(pillX + r, pillY + pillH);
    ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
    ctx.lineTo(pillX, pillY + r);
    ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, pillX + pillW / 2, pillY + pillH / 2);
};
